import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Field,
    FieldContent,
    FieldDescription,
    FieldTitle,
} from "@/components/ui/field";
import {
    RadioGroup,
    RadioGroupItem,
} from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import { createTool } from "@/api/tools/tools.api";
import { Tool } from "@/api/tools/tools.types";
import {
    Webhook,
    Hash,
    PhoneOff,
    PhoneForwarded,
    MessageSquare,
    Cpu,
    BookOpen,
    Voicemail,
    GitBranch,
    Loader2,
    CheckCircle2,
    AlertCircle,
    Wrench,
    Upload
} from "lucide-react";
import { readJsonFile, validateImportedConfig, processImportedConfig } from "@/utils/toolImportExport";
import { Separator } from "@/components/ui/separator";

interface CreateToolTypeDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const TOOL_TYPES = [
    {
        value: "function",
        label: "Function",
        icon: Webhook,
        description: "Execute custom functions via webhook"
    },
    {
        value: "dtmf",
        label: "DTMF",
        icon: Hash,
        description: "Handle DTMF input from users"
    },
    {
        value: "endCall",
        label: "End Call",
        icon: PhoneOff,
        description: "Terminate the current call session"
    },
    {
        value: "transferCall",
        label: "Transfer",
        icon: PhoneForwarded,
        description: "Transfer calls to another number"
    },
    {
        value: "output",
        label: "Output",
        icon: MessageSquare,
        description: "Send responses to the caller"
    },
    {
        value: "mcp",
        label: "MCP",
        icon: Cpu,
        description: "Execute MCP functions via webhook"
    },
    {
        value: "query",
        label: "QKnowledge",
        icon: BookOpen,
        description: "Query knowledge bases for data"
    },
    {
        value: "voicemail",
        label: "Voicemail",
        icon: Voicemail,
        description: "Leave voicemail when detected"
    },
    {
        value: "handoff",
        label: "TransAssist",
        icon: GitBranch,
        description: "Transfer to another assistant"
    }
] as const;

export function CreateToolTypeDialog({ open, onOpenChange }: CreateToolTypeDialogProps) {
    const navigate = useNavigate();
    const { success, error } = useEnhancedToast();
    const [selectedType, setSelectedType] = useState<Tool['type']>("function");
    const [isCreating, setIsCreating] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    const handleImport = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) return;

            setIsImporting(true);
            try {
                const importedData = await readJsonFile(file);
                const validation = validateImportedConfig(importedData);

                // Show warnings if any
                if (validation.warnings.length > 0) {
                    validation.warnings.forEach(warning => {
                        success({
                            icon: AlertCircle,
                            title: "Import Warning",
                            description: warning,
                            duration: 3000
                        });
                    });
                }

                if (!validation.isValid) {
                    error({
                        icon: AlertCircle,
                        title: "Import Failed",
                        description: validation.errors.join('\n'),
                        duration: 6000
                    });
                    return;
                }

                // Process the configuration
                const processedConfig = processImportedConfig(importedData);

                // Reset state first
                setIsImporting(false);
                
                // Close dialog
                onOpenChange(false);
                
                // Navigate immediately after closing dialog - use requestAnimationFrame to ensure DOM updates
                requestAnimationFrame(() => {
                    navigate('/dashboard/tools/new', {
                        state: { 
                            importedToolData: processedConfig
                        }
                    });
                    
                    // Show success toast after navigation
                    setTimeout(() => {
                        success({
                            icon: Upload,
                            title: "Configuration Imported",
                            description: `Successfully imported tool configuration. Review and save to create the tool.`,
                            duration: 4000
                        });
                    }, 200);
                });
                
                return; // Exit early to skip finally block

            } catch (err) {
                error({
                    icon: AlertCircle,
                    title: "Import Failed",
                    description: err instanceof Error ? err.message : "Failed to import configuration",
                    duration: 4000
                });
            } finally {
                setIsImporting(false);
            }
        };
        input.click();
    };

    const handleCreate = async () => {
        setIsCreating(true);
        try {
            // Create tool with just the type
            const response = await createTool({
                type: selectedType,
                async: false,
                function: null,
                server: null,
                messages: [],
                destinations: []
            } as Partial<Tool> as Tool);

            if (response.error) throw new Error(response.error);

            success({
                icon: CheckCircle2,
                title: "Tool Created",
                description: "Your new tool has been created. Configure it now.",
                duration: 3000,
            });

            // Navigate to edit page
            if (response.data?.id) {
                onOpenChange(false);
                navigate(`/dashboard/tools/${response.data.id}`);
            }
        } catch (err) {
            error({
                icon: AlertCircle,
                title: "Creation Failed",
                description: err instanceof Error ? err.message : "Failed to create tool",
                duration: 4000,
            });
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0">
                <DialogHeader className="pb-3 px-6 pt-4 border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                            <Wrench className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 space-y-0.5">
                            <DialogTitle className="text-base">Create New Tool</DialogTitle>
                            <DialogDescription className="text-xs text-muted-foreground/70">
                                Select the type of tool you want to create, or import an existing configuration.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto py-6 px-6">
                    <RadioGroup
                        value={selectedType}
                        onValueChange={(value) => setSelectedType(value as Tool['type'])}
                        className="grid grid-cols-1 md:grid-cols-2 gap-3"
                    >
                        {TOOL_TYPES.map((type) => {
                            const Icon = type.icon;
                            return (
                                <Label
                                    key={type.value}
                                    htmlFor={type.value}
                                    className="cursor-pointer"
                                >
                                    <Field orientation="horizontal">
                                        <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 flex items-center justify-center ring-1 ring-primary/10 shadow-sm shrink-0">
                                            <Icon className="h-5 w-5 text-primary" />
                                        </div>
                                        <FieldContent>
                                            <FieldTitle>{type.label}</FieldTitle>
                                            <FieldDescription>
                                                {type.description}
                                            </FieldDescription>
                                        </FieldContent>
                                        <RadioGroupItem
                                            value={type.value}
                                            id={type.value}
                                        />
                                    </Field>
                                </Label>
                            );
                        })}
                    </RadioGroup>
                </div>

                <DialogFooter className="py-3 px-6 border-t bg-muted/20">
                    <div className="flex items-center justify-between w-full">
                        <Button
                            variant="outline"
                            onClick={handleImport}
                            disabled={isCreating || isImporting}
                            size="sm"
                            className="gap-2"
                        >
                            {isImporting ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Importing
                                </>
                            ) : (
                                <>
                                    <Upload className="h-4 w-4" />
                                    Import
                                </>
                            )}
                        </Button>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={isCreating || isImporting}
                                size="sm"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleCreate}
                                disabled={isCreating || isImporting}
                                size="sm"
                                className="gap-2"
                            >
                                {isCreating ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Creating
                                    </>
                                ) : (
                                    "Create Tool"
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

